import {modalHeaderVariantsConfig, modalSizeVariants} from './modal.config';
import {ModalIcon} from './ModalIcon';
import {useDiscardGuard} from './useDiscardGuard';
import {cn} from '../../lib/utils';
import {Button} from '../shadcn/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../shadcn/dialog';
import {typographyVariantsConfig} from '../Typography';

import type {
  ModalHeaderVariant,
  ModalIconVariant,
  ModalOutsidePress,
  ModalSize
} from './modal.types';
import type {IconName} from '../../icons/icon-map';
import type React from 'react';
import type {ReactNode} from 'react';

export {ModalIcon, modalIconVariants, modalIconVariantsConfig} from './ModalIcon';
export {
  modalHeaderVariantsConfig,
  modalSizeVariants,
  modalSizeVariantsConfig
} from './modal.config';
export type {ModalIconProps} from './ModalIcon';
export type {
  ModalHeaderVariant,
  ModalIconVariant,
  ModalOutsidePress,
  ModalSize
} from './modal.types';
export {DialogClose as ModalClose} from '../shadcn/dialog';

export interface ModalProps {
  /** Controls open state (controlled mode) */
  isOpen?: boolean;
  /** Called when the modal requests to be closed */
  onOpenChange?: (open: boolean) => void;
  /** Modal title — rendered in <DialogTitle> */
  title: ReactNode;
  /** Optional subtitle below the title */
  description?: ReactNode;
  /** Optional icon rendered above the title */
  icon?: IconName;
  /** Optional illustration rendered above the title. Use this instead of icon for custom visuals. */
  illustration?: ReactNode;
  /** Colour theme for the icon container circle */
  iconVariant?: ModalIconVariant;
  /** Maximum width of the dialog */
  size?: ModalSize;
  /** Body content (form fields, paragraphs, etc.) */
  children?: ReactNode;
  /** Custom footer. If omitted and primaryAction/secondaryAction are provided, a default footer is rendered. */
  footer?: ReactNode;
  /** Label for the primary (confirm) action button. When omitted, no footer is rendered. */
  primaryLabel?: string;
  /** Called when primary button is clicked */
  onPrimaryAction?: () => void;
  /** Whether the primary button is in a loading / disabled state */
  primaryDisabled?: boolean;
  /** Whether the primary button is loading */
  primaryLoading?: boolean;
  /** Variant for the primary button */
  primaryVariant?: 'primary' | 'destructive';
  /** Label for the secondary (cancel) action button. Defaults to "Cancel". */
  secondaryLabel?: string;
  /** Called when secondary button is clicked (defaults to closing the modal) */
  onSecondaryAction?: () => void;
  /**
   * Force header title/description alignment regardless of icon/illustration presence.
   * Defaults to the existing behavior: `start` when a visual is present, `center` otherwise.
   */
  headerAlign?: 'start' | 'center';
  /** Hide the default X close button in the top corner */
  showCloseButton?: boolean;
  /** Additional class on the dialog content panel */
  className?: string;
  /** `hero` is a larger, onboarding-style header (bigger title, narrower description). */
  headerVariant?: ModalHeaderVariant;
  /** Rendered inside the header, below the description — shares the header's own gap. */
  headerAddon?: ReactNode;
  /** Rendered at the very top of the header, above the icon/illustration and title. */
  headerTop?: ReactNode;
  /** Overrides `headerVariant`'s title size, on the Typography scale. */
  titleSize?: keyof typeof typographyVariantsConfig.variants.size;
  titleWeight?: keyof typeof typographyVariantsConfig.variants.weight;
  /** Disable focus trap and scroll lock. Set to true in Storybook to keep docs interactive. Defaults to false. */
  disableFocusTrap?: boolean;
  /** Container element for the portal. Pass a ref to a div to keep the portal inside the story (Storybook use case). */
  portalContainer?: React.RefObject<HTMLElement | null> | HTMLElement | null;
  outsidePress?: ModalOutsidePress;
  /** When false, every close attempt (Escape, outside press) nudges instead and the X is hidden. */
  dismissible?: boolean;
  isDirty?: boolean;
  discardDescription?: string;
}

export function Modal({
  isOpen,
  onOpenChange,
  title,
  description,
  icon,
  illustration,
  iconVariant = 'brand',
  size = 'md',
  children,
  footer,
  primaryLabel,
  onPrimaryAction,
  primaryDisabled = false,
  primaryLoading = false,
  primaryVariant = 'primary',
  secondaryLabel = 'Cancel',
  onSecondaryAction,
  headerAlign,
  showCloseButton = true,
  className,
  headerVariant = 'default',
  headerAddon,
  headerTop,
  titleSize,
  titleWeight,
  disableFocusTrap = false,
  portalContainer,
  outsidePress,
  dismissible = true,
  isDirty = false,
  discardDescription
}: ModalProps) {
  const {handleOpenChange, discardModal} = useDiscardGuard({
    isOpen,
    isDirty,
    onOpenChange,
    discardDescription
  });
  const hasDefaultFooter = !footer && (!!primaryLabel || onSecondaryAction !== undefined);
  const hasVisual = Boolean(icon ?? illustration);
  const isCentered = headerAlign ? headerAlign === 'center' : !hasVisual;
  const headerStyles = modalHeaderVariantsConfig[headerVariant];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      modal={!disableFocusTrap}
      outsidePress={outsidePress}
      dismissible={dismissible}
    >
      <DialogContent
        showCloseButton={showCloseButton && dismissible}
        className={cn(modalSizeVariants({size}), className)}
        initialFocus={disableFocusTrap ? false : undefined}
        container={portalContainer}
      >
        <DialogHeader
          className={cn(
            headerStyles.header,
            isCentered ? 'items-center text-center' : 'items-start'
          )}
        >
          {headerTop}
          {illustration ? (
            <div data-slot="modal-illustration">{illustration}</div>
          ) : icon ? (
            <ModalIcon icon={icon} variant={iconVariant} />
          ) : null}
          <div className={cn('flex flex-col', headerStyles.text, isCentered && 'items-center')}>
            <DialogTitle
              className={cn(
                headerStyles.title,
                titleSize && typographyVariantsConfig.variants.size[titleSize],
                titleWeight && typographyVariantsConfig.variants.weight[titleWeight],
                isCentered && 'text-center'
              )}
            >
              {title}
            </DialogTitle>
            {description && (
              <DialogDescription
                className={cn(headerStyles.description, isCentered && 'text-center')}
              >
                {description}
              </DialogDescription>
            )}
          </div>
          {headerAddon}
        </DialogHeader>

        {children && (
          <div data-slot="modal-body" className="-mx-6 px-6 min-h-0 flex-1 overflow-y-auto">
            {children}
          </div>
        )}
        {footer ? (
          <DialogFooter>{footer}</DialogFooter>
        ) : hasDefaultFooter ? (
          <DialogFooter className="justify-end">
            {/* While dirty, the close runs through the confirm and ends in onOpenChange(false). */}
            {onSecondaryAction && (
              <DialogClose
                render={
                  <Button variant="secondary" onClick={isDirty ? undefined : onSecondaryAction} />
                }
              >
                {secondaryLabel}
              </DialogClose>
            )}
            {primaryLabel && (
              <Button
                variant="primary"
                destructive={primaryVariant === 'destructive'}
                disabled={primaryDisabled}
                isLoading={primaryLoading}
                onClick={onPrimaryAction}
                iconPosition="end"
              >
                {primaryLabel}
              </Button>
            )}
          </DialogFooter>
        ) : null}
        {discardModal}
      </DialogContent>
    </Dialog>
  );
}
